<template>
  <div>
    <DataTable
        class="mt-5"
        :loading="isLoading"
        :value="currentTabState.tableData"
        :rows="currentTabState.rowsPerPage"
        :first="currentTabState.first"
        :totalRecords="currentTabState.totalRecords"
        :sortField="currentTabState.sortField"
        :sortOrder="currentTabState.sortOrder"
        selectionMode="single"
        @row-select="onRowSelect"
        @row-click="onRowClick"
        @page="onPageChange"
        @sort="onSort"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="$rowsPerPageOptions"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        :lazy="true"
        :rowClass="getRowClass"
        :showGridlines="false"
        :stripedRows="true"
        :dataKey="dataKey"
        :expandedRows.sync="expandedRows"
        @row-expand="onRowExpand"
        @row-collapse="onRowCollapse"
    >
      <Column v-if="enableRowExpansion" :expander="true" :headerStyle="{ width: '3rem' }" />

      <Column
          v-for="col in columns"
          :key="col.field"
          :field="col.field"
          :header="col.header"
          :sortable="col.sortable"
          :headerStyle="{ width: col.width }"
      >
        <template v-if="col.custom" #body="slotProps">
          <slot :name="col.slotName" :rowData="slotProps.data" />
        </template>

        <template v-else-if="col.field === 'schedule_day'" #body="slotProps">
          <span v-if="slotProps.data._isGroupStart">{{ localScheduleDay(slotProps.data.scheduled_date) }}</span>
        </template>

        <template v-else-if="col.field === 'schedule_date'" #body="slotProps">
          <span v-if="slotProps.data._isGroupStart">{{ localScheduleDate(slotProps.data.scheduled_date) }}</span>
        </template>
      </Column>

      <template #expansion="slotProps" v-if="enableRowExpansion">
        <div class="nested-wrap">
          <div class="nested-title">EQUIPMENT</div>

          <DataTable
              class="nested-table"
              :value="slotProps.data.equipments"
              responsiveLayout="scroll"
              :showGridlines="false"
              :stripedRows="true"
          >
            <Column field="product_code" header="Code" :headerStyle="{ width: '12%' }" />
            <Column field="name" header="Description" :headerStyle="{ width: '26%' }" :bodyStyle="{ maxWidth: '0' }">
              <template #body="sp">
                <div class="truncate" :title="sp.data.name || ''">{{ sp.data.name || "-" }}</div>
              </template>
            </Column>
            <Column field="serial_number" header="Serial No." :headerStyle="{ width: '14%' }">
              <template #body="sp">
                {{ sp.data.serial_number || "-" }}
              </template>
            </Column>
            <Column field="make" header="Make" :headerStyle="{ width: '10%' }">
              <template #body="sp">
                {{ sp.data.make || "-" }}
              </template>
            </Column>
            <Column field="model_name" header="Model" :headerStyle="{ width: '10%' }">
              <template #body="sp">
                {{ sp.data.model_name || "-" }}
              </template>
            </Column>
            <Column field="service_agent" header="Service Agent" :headerStyle="{ width: '14%' }">
              <template #body="sp">
                {{ sp.data.service_agent || "-" }}
              </template>
            </Column>
            <Column header="Job Type" :headerStyle="{ width: '14%' }">
              <template #body="sp">
                <span class="jobtype-pill">
                  {{ (sp.data.job_types && sp.data.job_types.length) ? sp.data.job_types.join(", ") : "-" }}
                </span>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>

      <template #empty>
        No records found.
      </template>
    </DataTable>

    <Paginator
        :first.sync="currentTabState.first"
        template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        @page="onPageChange"
        :rows.sync="currentTabState.rowsPerPage"
        :totalRecords="currentTabState.totalRecords"
        :rowsPerPageOptions="$rowsPerPageOptions"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { localScheduleDate, localScheduleDay } from "@/utils/scheduleDateFormat";

export default {
  props: {
    columns: { type: Array, required: true },
    enableRowClick: { type: Boolean, default: true },
    enableRowExpansion: { type: Boolean, default: false },
    dataKey: { type: String, default: "job_id" }
  },

  data() {
    return {
      expandedRows: []
    };
  },

  computed: {
    ...mapState("mySchedule", {
      currentTabState: (state) => state.tabs[state.currentTab],
      isLoading: (state) => state.isLoading
    })
  },

  methods: {
    ...mapActions("mySchedule", ["updateTableSettings", "updateSortOptions"]),
    localScheduleDate,
    localScheduleDay,

    onRowSelect(event) {
      this.$emit("row-select", event);
    },

    onRowClick(event) {
      if (this.enableRowClick) this.$emit("row-click", event);
    },

    onPageChange(event) {
      this.expandedRows = [];
      this.updateTableSettings({ rows: event.rows, page: event.page });
    },

    onSort(event) {
      this.expandedRows = [];
      this.updateSortOptions({ field: event.sortField, order: event.sortOrder });
    },

    // Only one open at a time
    onRowExpand(event) {
      if (!event || !event.data) return;
      this.expandedRows = [event.data];
      this.$emit("row-expand", event);
    },

    onRowCollapse(event) {
      this.expandedRows = [];
      this.$emit("row-collapse", event);
    },

    getRowClass(rowData) {
      const classes = [];

      if (rowData._groupIndex === 0) classes.push("group-a");
      else classes.push("group-b");

      if (rowData._isGroupStart) classes.push("group-top-border");
      if (rowData._isGroupEnd) classes.push("group-bottom-border");

      // hide inner borders for grouped Day/Date rows (visual rowspan)
      if (!rowData._isGroupStart) classes.push("group-continuation");

      // Confirmed jobs go light green, the same as on the Scheduled tab
      if (rowData.is_confirmed === true) classes.push("row-scheduled");

      return classes.join(" ");
    }
  }
};
</script>

<style scoped>
::v-deep .p-datatable-wrapper,
::v-deep .p-datatable-table {
  width: 100% !important;
  min-width: 100% !important;
  border-collapse: collapse !important;
}

/* default row borders */
::v-deep .p-datatable-tbody > tr > td {
  border: 1px solid #e9ecef !important;
  border-width: 0 0 1px 0 !important;
}

/* Day column (2nd td) top aligned + left/right border */
::v-deep .p-datatable-tbody > tr > td:nth-child(2) {
  vertical-align: top !important;
  border-left: 1px solid #e9ecef !important;
  border-right: 1px solid #e9ecef !important;
}

/* Visual rowspan effect: when continuation row, remove the Day/Date borders so it looks merged */
::v-deep .p-datatable-tbody > tr.group-continuation > td:nth-child(2),
::v-deep .p-datatable-tbody > tr.group-continuation > td:nth-child(3) {
  border-top: 0 !important;
  border-bottom: 0 !important;
}

/* Expansion row must be clean */
::v-deep .p-datatable-tbody > tr.p-datatable-row-expansion > td {
  padding: 0 !important;
  background-color: #f2f4f6 !important;
  border: 0 !important;
}

/* Group background */
::v-deep .group-a > td {
  background-color: #fcfcfc !important;
}
::v-deep .group-b > td {
  background-color: #f2f4f6 !important;
}
/* Confirmed job: wins over the group background */
::v-deep .row-scheduled > td {
  background-color: lightgreen !important;
}
::v-deep .group-top-border > td {
  border-top: 2px solid #c0c0c0 !important;
}
::v-deep .group-bottom-border > td {
  border-bottom: 2px solid #c0c0c0 !important;
}

/* Nested panel */
.nested-wrap {
  border: 1px solid #c0c0c0;
  background: #fbfbfc;
  border-radius: 6px;
  padding: 12px 12px 6px 12px;
  margin: 8px 10px 10px 10px;
}

.nested-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #b45309;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid #f59e0b;
}

::v-deep .nested-table .p-datatable-thead > tr > th {
  background-color: #fff7ed !important;
  border-bottom: 1px solid #f3f4f6 !important;
}
::v-deep .nested-table .p-datatable-tbody > tr > td {
  background-color: transparent !important;
  border-color: #eef2f7 !important;
}
::v-deep .nested-table .p-datatable-thead > tr {
  border-bottom: 0 !important;
}
::v-deep .nested-table .p-datatable-tbody > tr:last-child,
::v-deep .nested-table .p-datatable-tbody > tr:last-child > td {
  border-bottom: 0 !important;
}

.jobtype-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #eef2ff;
  color: #3730a3;
}
</style>
